import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../../api/client'
import type { DnsPiano, DnsPtrEsito, DnsRecord, DnsScritturaEsito } from '../../api/types'
import { ErrorBanner, Spinner } from '../common'
import { t } from '../../i18n/lingua'
import {
  CAMPI_VUOTI, TIPI_SCRIVIBILI, TTL_PRESET, componi, descrizioneTipo, formatTtl, nomeAssoluto,
  nomePtrDaIp, nomeRelativo, problemi, scomponi, zonaInversa,
} from './dnsUtil'
import type { CampiRecord } from './dnsUtil'

/**
 * The record editor, in two steps.
 *
 * 1. **The record**: the type, the name relative to the zone and the value in
 *    its own fields (an address, a target, the MX priority, the four SRV
 *    numbers), the TTL from presets, and a preview of the line that will be
 *    written. Errors are spelled out before anything leaves the browser.
 * 2. **The confirmation**, from the *plan* the Control Plane reads live from the
 *    server (`…/records/plan`): the values the name already has («add next to
 *    them or replace them?»), the conflicts the DNS would refuse (a CNAME does
 *    not live with other records), and for A/AAAA the PTR of the address —
 *    missing, already right, or pointing elsewhere — with the choice to create
 *    or update it; when the address changes, the old PTR to remove.
 *
 * The write then carries the choices; a PTR that cannot be written does not
 * undo the record, and the summary says so.
 */
export function EditorRecord({
  zona,
  zoneId,
  server,
  record,
  tipoIniziale,
  onChiudi,
  onFatto,
}: {
  zona: string
  zoneId: string
  server: string | null
  record?: DnsRecord
  tipoIniziale?: string
  onChiudi: () => void
  onFatto: (messaggio: string) => void
}) {
  const modifica = Boolean(record)
  const inversa = zonaInversa(zona)
  const [tipo, setTipo] = useState(record?.type ?? tipoIniziale ?? (inversa ? 'PTR' : 'A'))
  const [nome, setNome] = useState(record ? nomeRelativo(record.name, zona) : '')
  const [campi, setCampi] = useState<CampiRecord>(record ? scomponi(record.type ?? '', record.content) : CAMPI_VUOTI)
  const ttlIniziale = record?.ttl ?? 3600
  const [ttlScelta, setTtlScelta] = useState(TTL_PRESET.includes(ttlIniziale) ? String(ttlIniziale) : 'altro')
  const [ttlAltro, setTtlAltro] = useState(String(ttlIniziale))
  const [ipPtr, setIpPtr] = useState('')
  const ttl = ttlScelta === 'altro' ? Number(ttlAltro) : Number(ttlScelta)

  const [passo, setPasso] = useState<'dati' | 'conferma'>('dati')
  const [piano, setPiano] = useState<DnsPiano | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  // The choices of the confirmation step.
  const [sostituisci, setSostituisci] = useState(false)
  const [sceltaPtr, setSceltaPtr] = useState<'none' | 'create' | 'replace'>('none')
  const [togliPtrVecchio, setTogliPtrVecchio] = useState(true)

  // A PTR typed as an address: the name relative to the reverse zone follows.
  useEffect(() => {
    if (tipo !== 'PTR' || !ipPtr.trim()) return
    const n = nomePtrDaIp(ipPtr, zona)
    if (n) setNome(n)
  }, [ipPtr, tipo, zona])

  const contenuto = componi(tipo, campi)
  const errori = useMemo(() => {
    const e = problemi(tipo, nome, campi, ttl)
    if (tipo === 'PTR' && ipPtr.trim() && !nomePtrDaIp(ipPtr, zona)) e.push(t('L’indirizzo non appartiene alla zona inversa {zona}.', { zona }))
    if (!nome.trim() && !modifica) e.push(t('Scrivi il nome (o @ per la zona stessa).'))
    return e
  }, [tipo, nome, campi, ttl, ipPtr, zona, modifica])
  const assoluto = nomeAssoluto(nome || '@', zona)
  const anteprima = `${assoluto}.  ${Number.isFinite(ttl) ? ttl : '?'}  IN  ${tipo}  ${contenuto || '…'}`

  const imposta = (k: keyof CampiRecord) => (v: string) => setCampi((c) => ({ ...c, [k]: v }))

  async function continua(e: FormEvent) {
    e.preventDefault()
    if (errori.length) return
    setErrore(null)
    setLavoro(true)
    try {
      const p = await api.dnsPlan(zoneId, {
        action: modifica ? 'update' : 'add', name: nome || '@', type: tipo, content: contenuto,
        old_content: record?.content ?? undefined,
      }, server)
      setPiano(p)
      // Defaults: an address replaces (an A that changes, not a second one),
      // the values that usually come in sets (MX, NS, TXT, SRV) are added.
      setSostituisci(['A', 'AAAA', 'CNAME', 'PTR'].includes(tipo) && p.same_type.length > 0)
      setSceltaPtr(p.ptr?.status === 'missing' ? 'create' : p.ptr?.status === 'different' ? 'replace' : 'none')
      setTogliPtrVecchio(p.old_ptr?.status === 'ok')
      setPasso('conferma')
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  async function scrivi() {
    setErrore(null)
    setLavoro(true)
    try {
      const corpo = { name: nome || '@', type: tipo, content: contenuto, ttl, ptr: sceltaPtr }
      const esito: DnsScritturaEsito = modifica && record
        ? await api.dnsUpdateRecord(zoneId, { ...corpo, old_content: record.content ?? '', remove_old_ptr: togliPtrVecchio }, server)
        : await api.dnsAddRecord(zoneId, { ...corpo, replace: sostituisci }, server)
      onFatto(riepilogo(modifica, assoluto, tipo, contenuto, esito))
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  const bloccato = piano?.conflict != null && !(piano.conflict === 'duplicate' && sostituisci)

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={modifica ? t('Modifica record') : t('Nuovo record')}>
      <div className="dob-modal__scrim" onClick={lavoro ? undefined : onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(680px, 100%)' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{modifica ? t('Modifica record') : t('Nuovo record in {zona}', { zona })}</h3>
            <p className="dob-modal__sotto">
              {passo === 'dati'
                ? t('Passo 1 di 2: il record. Prima di scrivere, CENSO controlla sul server che cosa c’è già.')
                : t('Passo 2 di 2: che cosa succede sul server. Niente è ancora stato scritto.')}
            </p>
          </div>
          <button className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Chiudi')}</button>
        </div>
        <ErrorBanner error={errore} />

        {passo === 'dati' ? (
          <form onSubmit={continua} className="dns-editor">
            {!modifica && (
              <div className="dns-tipi" role="radiogroup" aria-label={t('Tipo')}>
                {TIPI_SCRIVIBILI.map((x) => (
                  <button
                    key={x}
                    type="button"
                    role="radio"
                    aria-checked={tipo === x}
                    className={`dns-tipo-scelta${tipo === x ? ' dns-tipo-scelta--attiva' : ''}`}
                    onClick={() => { setTipo(x); setCampi(CAMPI_VUOTI) }}
                    title={descrizioneTipo(x)}
                  >
                    <strong>{x}</strong>
                    <span>{descrizioneTipo(x)}</span>
                  </button>
                ))}
              </div>
            )}
            {modifica && <p className="muted">{t('{tipo}: {descrizione}', { tipo, descrizione: descrizioneTipo(tipo) })}</p>}

            {tipo === 'PTR' && inversa && !modifica && (
              <label className="field">
                <span className="field__label">{t('Indirizzo')}</span>
                <input type="text" value={ipPtr} onChange={(e) => setIpPtr(e.target.value)} placeholder={t('per esempio 10.0.0.5')} />
                <span className="field__hint">{t('Scrivi l’indirizzo: il nome del PTR nella zona si calcola da solo.')}</span>
              </label>
            )}

            <label className="field">
              <span className="field__label">{t('Nome')}</span>
              <div className="dns-nome">
                <input type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  readOnly={modifica}
                  placeholder={tipo === 'SRV' ? '_ldap._tcp' : t('www, oppure @ per la zona')}
                  autoFocus={!modifica}
                />
                <span className="dns-nome__zona">.{zona}</span>
              </div>
              {tipo === 'SRV' && <span className="field__hint">{t('Per un SRV il nome è servizio e protocollo, per esempio «_ldap._tcp» o «_sip._udp».')}</span>}
            </label>

            {(tipo === 'A' || tipo === 'AAAA') && (
              <label className="field">
                <span className="field__label">{tipo === 'A' ? t('Indirizzo IPv4') : t('Indirizzo IPv6')}</span>
                <input type="text" value={campi.ip} onChange={(e) => imposta('ip')(e.target.value)} placeholder={tipo === 'A' ? '10.0.0.5' : '2001:db8::5'} />
              </label>
            )}
            {(tipo === 'MX' || tipo === 'SRV') && (
              <div className="row">
                <label className="field" style={{ maxWidth: 140 }}>
                  <span className="field__label">{t('Priorità')}</span>
                  <input type="number" min={0} max={65535} value={campi.priorita} onChange={(e) => imposta('priorita')(e.target.value)} />
                </label>
                {tipo === 'SRV' && (
                  <>
                    <label className="field" style={{ maxWidth: 140 }}>
                      <span className="field__label">{t('Peso')}</span>
                      <input type="number" min={0} max={65535} value={campi.peso} onChange={(e) => imposta('peso')(e.target.value)} />
                    </label>
                    <label className="field" style={{ maxWidth: 140 }}>
                      <span className="field__label">{t('Porta')}</span>
                      <input type="number" min={0} max={65535} value={campi.porta} onChange={(e) => imposta('porta')(e.target.value)} placeholder="389" />
                    </label>
                  </>
                )}
              </div>
            )}
            {['CNAME', 'NS', 'PTR', 'MX', 'SRV'].includes(tipo) && (
              <label className="field">
                <span className="field__label">
                  {tipo === 'CNAME' ? t('Alias di') : tipo === 'MX' ? t('Server di posta') : tipo === 'NS' ? t('Server dei nomi') : tipo === 'PTR' ? t('Nome host') : t('Destinazione')}
                </span>
                <input type="text" value={campi.bersaglio} onChange={(e) => imposta('bersaglio')(e.target.value)} placeholder={t('server1.esempio.it')} />
              </label>
            )}
            {tipo === 'TXT' && (
              <label className="field">
                <span className="field__label">{t('Testo')}</span>
                <textarea rows={3} value={campi.testo} onChange={(e) => imposta('testo')(e.target.value)} placeholder={t('v=spf1 include:esempio.it -all')} />
                <span className="field__hint">{t('Senza virgolette: le mette il server se servono.')}</span>
              </label>
            )}

            <div className="row">
              <label className="field" style={{ maxWidth: 220 }}>
                <span className="field__label">TTL</span>
                <select value={ttlScelta} onChange={(e) => setTtlScelta(e.target.value)}>
                  {TTL_PRESET.map((p) => <option key={p} value={String(p)}>{formatTtl(p)}</option>)}
                  <option value="altro">{t('Altro…')}</option>
                </select>
              </label>
              {ttlScelta === 'altro' && (
                <label className="field" style={{ maxWidth: 180 }}>
                  <span className="field__label">{t('Secondi')}</span>
                  <input type="number" min={1} value={ttlAltro} onChange={(e) => setTtlAltro(e.target.value)} />
                </label>
              )}
            </div>

            <div className="field">
              <span className="field__label">{t('Anteprima')}</span>
              <div className="dns-anteprima">{anteprima}</div>
            </div>
            {errori.length > 0 && (nome || contenuto) && (
              <ul className="dns-errori">{errori.map((x) => <li key={x}>{x}</li>)}</ul>
            )}
            <div className="dob-modal__comandi" style={{ marginTop: 12 }}>
              <button type="submit" disabled={lavoro || errori.length > 0}>
                {lavoro ? t('Controllo sul server…') : t('Continua')}
              </button>
              <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
            </div>
          </form>
        ) : piano && (
          <div className="dns-editor">
            <div className="dns-anteprima">{anteprima}</div>

            {piano.conflict === 'cname_alone' && (
              <div className="banner banner--error">
                {t('{nome} ha già altri record ({tipi}): un CNAME deve essere l’unico record del suo nome. Togli prima gli altri, o scegli un altro nome.', { nome: piano.fqdn, tipi: [...piano.other_types, ...(piano.same_type.length ? ['CNAME'] : [])].join(', ') })}
              </div>
            )}
            {piano.conflict === 'name_has_cname' && (
              <div className="banner banner--error">
                {t('{nome} è un alias (CNAME): non può avere altri record. Scrivi il record sul nome a cui punta, o togli prima il CNAME.', { nome: piano.fqdn })}
              </div>
            )}
            {piano.conflict === 'duplicate' && (
              <div className="banner banner--warn">
                {modifica
                  ? t('{nome} ha già un record {tipo} con questo valore.', { nome: piano.fqdn, tipo })
                  : t('Il record {nome} {tipo} {valore} esiste già: non c’è niente da aggiungere.', { nome: piano.fqdn, tipo, valore: contenuto })}
              </div>
            )}

            {!modifica && piano.same_type.length > 0 && piano.conflict == null && (
              <fieldset className="dns-scelta">
                <legend>{t('{nome} ha già {n} record {tipo}', { nome: piano.fqdn, n: piano.same_type.length, tipo })}</legend>
                <p className="muted dns-valori">{piano.same_type.join(' · ')}</p>
                <label className="radio">
                  <input type="radio" checked={sostituisci} onChange={() => setSostituisci(true)} />{' '}
                  {t('Aggiorna: sostituisci con {valore}', { valore: contenuto })}
                </label>
                <label className="radio">
                  <input type="radio" checked={!sostituisci} onChange={() => setSostituisci(false)} />{' '}
                  {t('Aggiungi accanto (il nome avrà {n} valori)', { n: piano.same_type.length + 1 })}
                </label>
              </fieldset>
            )}

            {piano.ptr && <SceltaPtr stato={piano.ptr} nome={piano.fqdn} scelta={sceltaPtr} onScelta={setSceltaPtr} />}

            {piano.old_ptr?.status === 'ok' && (
              <fieldset className="dns-scelta">
                <legend>{t('Il vecchio indirizzo')}</legend>
                <label className="checkbox">
                  <input type="checkbox" checked={togliPtrVecchio} onChange={(e) => setTogliPtrVecchio(e.target.checked)} />{' '}
                  {t('Togli il PTR di {ip} (punta a questo nome, nella zona {zona})', { ip: piano.old_ptr.ip, zona: piano.old_ptr.zone ?? '' })}
                </label>
              </fieldset>
            )}

            <div className="dob-modal__comandi" style={{ marginTop: 12 }}>
              <button onClick={() => void scrivi()} disabled={lavoro || bloccato}>
                {lavoro ? t('Scrivo sul DNS…') : t('Scrivi sul DNS')}
              </button>
              <button className="secondary" onClick={() => setPasso('dati')} disabled={lavoro}>{t('Indietro')}</button>
            </div>
          </div>
        )}
        {lavoro && passo === 'dati' && <Spinner label={t('Controllo sul server…')} />}
      </div>
    </div>
  )
}

/** The PTR part of the confirmation: what the reverse zone says, and the choice. */
function SceltaPtr({ stato, nome, scelta, onScelta }: {
  stato: NonNullable<DnsPiano['ptr']>
  nome: string
  scelta: 'none' | 'create' | 'replace'
  onScelta: (s: 'none' | 'create' | 'replace') => void
}) {
  return (
    <fieldset className="dns-scelta">
      <legend>{t('Il PTR di {ip}', { ip: stato.ip })}</legend>
      {stato.status === 'ok' && <p className="muted">{t('Punta già a {nome}: niente da fare.', { nome })}</p>}
      {stato.status === 'no_zone' && (
        <p className="muted">{t('Il server non ha una zona inversa per {ip}: il PTR non si può creare qui.', { ip: stato.ip })}</p>
      )}
      {stato.status === 'error' && (
        <p className="banner banner--warn">{t('Non riesco a leggere il PTR ({motivo}): lo lascio com’è.', { motivo: stato.error ?? '' })}</p>
      )}
      {stato.status === 'missing' && (
        <label className="checkbox">
          <input type="checkbox" checked={scelta === 'create'} onChange={(e) => onScelta(e.target.checked ? 'create' : 'none')} />{' '}
          {t('Crea il PTR {ip} → {nome} nella zona {zona}', { ip: stato.ip, nome, zona: stato.zone ?? '' })}
        </label>
      )}
      {stato.status === 'different' && (
        <>
          <p className="muted dns-valori">{t('Oggi punta a: {valori}', { valori: stato.current.join(' · ') })}</p>
          <label className="radio">
            <input type="radio" checked={scelta === 'replace'} onChange={() => onScelta('replace')} />{' '}
            {t('Aggiorna il PTR: {ip} → {nome}', { ip: stato.ip, nome })}
          </label>
          <label className="radio">
            <input type="radio" checked={scelta === 'create'} onChange={() => onScelta('create')} />{' '}
            {t('Aggiungi un secondo PTR, lasciando quello di oggi')}
          </label>
          <label className="radio">
            <input type="radio" checked={scelta === 'none'} onChange={() => onScelta('none')} />{' '}
            {t('Lascia il PTR com’è')}
          </label>
        </>
      )}
    </fieldset>
  )
}

function frasePtr(p: DnsPtrEsito | null | undefined, tolto = false): string | null {
  if (!p) return null
  if (p.error) return t('PTR di {ip} non scritto: {motivo}', { ip: p.ip, motivo: p.error })
  if (!p.done) return null
  if (tolto) return t('tolto il PTR di {ip}', { ip: p.ip })
  return p.status === 'ok' ? null : t('PTR {ip} scritto in {zona}', { ip: p.ip, zona: p.zone ?? '' })
}

/** One line that says everything the write did, PTR included. */
export function riepilogo(modifica: boolean, nome: string, tipo: string, valore: string, esito: DnsScritturaEsito): string {
  const parti = [
    modifica
      ? t('Record modificato: {nome} {tipo} {valore}', { nome, tipo, valore })
      : t('Record aggiunto: {nome} {tipo} {valore}', { nome, tipo, valore }),
  ]
  if (esito.replaced?.length) parti.push(t('sostituiti: {valori}', { valori: esito.replaced.join(', ') }))
  const ptr = frasePtr(esito.ptr)
  if (ptr) parti.push(ptr)
  const tolto = frasePtr(esito.ptr_removed, true)
  if (tolto) parti.push(tolto)
  return parti.join(' · ')
}

/**
 * Removing a record: for an A/AAAA the plan says whether the address has a PTR
 * pointing to this name, and the removal can take it along.
 */
export function TogliRecord({ zona, zoneId, server, record, onChiudi, onFatto }: {
  zona: string
  zoneId: string
  server: string | null
  record: DnsRecord
  onChiudi: () => void
  onFatto: (messaggio: string) => void
}) {
  const indirizzo = record.type === 'A' || record.type === 'AAAA'
  const [piano, setPiano] = useState<DnsPiano | null>(null)
  const [carico, setCarico] = useState(indirizzo)
  const [togliPtr, setTogliPtr] = useState(true)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const nome = record.name ?? zona

  useEffect(() => {
    if (!indirizzo) return
    let vivo = true
    api.dnsPlan(zoneId, { action: 'delete', name: nome, type: record.type ?? '', content: record.content ?? '' }, server)
      .then((p) => { if (vivo) setPiano(p) })
      .catch(() => { /* senza piano si toglie il solo record */ })
      .finally(() => { if (vivo) setCarico(false) })
    return () => { vivo = false }
  }, [indirizzo, zoneId, nome, record.type, record.content, server])

  async function togli() {
    if (!window.confirm(t('Cancellare il record {tipo} «{nome}» dal DNS? La modifica va subito sul server DNS e non si può annullare.', { tipo: record.type ?? '', nome }))) return
    setErrore(null)
    setLavoro(true)
    try {
      const conPtr = piano?.old_ptr?.status === 'ok' && togliPtr
      const esito = await api.dnsDeleteRecord(zoneId, { name: nome, type: record.type ?? '', content: record.content ?? '', remove_ptr: conPtr }, server)
      const parti = [t('Record tolto: {nome} {tipo} {valore}', { nome, tipo: record.type ?? '', valore: record.content ?? '' })]
      const ptr = frasePtr(esito.ptr_removed, true)
      if (ptr) parti.push(ptr)
      onFatto(parti.join(' · '))
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Togli il record')}>
      <div className="dob-modal__scrim" onClick={lavoro ? undefined : onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(560px, 100%)' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Togli il record')}</h3>
            <p className="dob-modal__sotto">{t('Dalla zona {zona}. Non si annulla: per rimetterlo va riscritto.', { zona })}</p>
          </div>
        </div>
        <ErrorBanner error={errore} />
        <div className="dns-anteprima">{`${nome}.  ${record.ttl ?? ''}  IN  ${record.type}  ${record.content}`}</div>
        {carico && <Spinner label={t('Controllo il PTR sul server…')} />}
        {piano?.old_ptr?.status === 'ok' && (
          <label className="checkbox" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={togliPtr} onChange={(e) => setTogliPtr(e.target.checked)} />{' '}
            {t('Togli anche il PTR di {ip}, che punta a questo nome (zona {zona})', { ip: piano.old_ptr.ip, zona: piano.old_ptr.zone ?? '' })}
          </label>
        )}
        {piano?.old_ptr?.status === 'different' && (
          <p className="muted" style={{ marginTop: 10 }}>{t('Il PTR di {ip} punta a un altro nome: resta com’è.', { ip: piano.old_ptr.ip })}</p>
        )}
        <div className="dob-modal__comandi" style={{ marginTop: 12 }}>
          <button className="danger" onClick={() => void togli()} disabled={lavoro || carico}>
            {lavoro ? t('Scrivo sul DNS…') : t('Togli')}
          </button>
          <button className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
        </div>
      </div>
    </div>
  )
}
